import OutsideClickHandler from '../../../utils/OutsideClickHandler';
import useResponsive from '../../../../hooks/useResponsive';
import Button from '../../../utils/Button';
import { useCallback, useMemo, useState } from 'react';
import useTranslation from '../../../../hooks/useTranslation';

function SizeSelector({
  name,
  value,
  options,
  onChange,
  placeholder,
  sizesMapping,
}) {
  const { isMobile } = useResponsive();
  const [selectOpen, setSelectOpen] = useState(false);
  const { TRANSLATIONS } = useTranslation();

  const handleSelectOption = useCallback(
    (option) => {
      if (!option.disabled) {
        onChange(option);
        setSelectOpen(false);
      }
    },
    [onChange]
  );

  const optionsMapped = useMemo(() => {
    if (sizesMapping.length === 0) {
      return options.map((option) => ({
        euSize: option.label,
        sizeOnLabel: null,
        value: option.value,
        disabled: option.disabled,
      }));
    }

    return options.map((option) => {
      const labelParts = option.label.split(' ');
      const label = labelParts[0];
      const sizeValue = labelParts[1] ? parseFloat(labelParts[1]) : null;

      const matchingSize = sizesMapping.find((sizes) =>
        sizes.some(
          (size) =>
            size.key === label &&
            (!sizeValue || parseFloat(size.value) === sizeValue)
        )
      );

      if (matchingSize) {
        const euSize = matchingSize.find((size) => size.key === 'EU');
        return {
          euSize: `${euSize.key} ${euSize.value}`,
          sizeOnLabel: option.label,
          value: option.value,
          disabled: option.disabled,
        };
      } else {
        return {
          euSize: option.label,
          sizeOnLabel: null,
          value: option.value,
          disabled: option.disabled,
        };
      }
    });
  }, [options, sizesMapping]);

  const getValue = useMemo(
    () => optionsMapped?.find((option) => option.value === value, [])?.euSize,
    [optionsMapped, value]
  );

  return (
    <>
      <div className={`${isMobile ? '' : 'relative'}`} key={name}>
        <OutsideClickHandler
          onOutsideClick={() => !isMobile && setSelectOpen(false)}
          key={name}
        >
          <Button
            onClick={() => {
              setSelectOpen((prev) => !prev);
            }}
            className={`radius-button large wide inline action ${
              selectOpen && 'focus'
            } ${selectOpen && 'bg-full'}`}
            key={name}
          >
            <div className="d-f label-filter g-10">
              <p>{getValue || placeholder}</p>
              <span
                className={
                  selectOpen ? 'icon-chevron-down active' : 'icon-chevron-down'
                }
              />
            </div>
          </Button>
          {isMobile && (
            <div
              className={`select-background ${
                selectOpen ? 'show-background' : ''
              }`}
            />
          )}
          <div
            className={`wrapper-filter d-f fd-c right-absolute ${
              selectOpen ? 'show' : ''
            }`}
          >
            <div className="filters-container">
              <OutsideClickHandler
                onOutsideClick={() => isMobile && setSelectOpen(false)}
                key={name}
              >
                {optionsMapped?.some((option) => option.sizeOnLabel) && (
                  <div className="options-divider" />
                )}
                <div className="wrapper-filter-header d-f ai-c">
                  <h5 className="d-f g-5 ai-c w-50">
                    {TRANSLATIONS.products.detail.size}
                  </h5>
                  {optionsMapped?.some((option) => option.sizeOnLabel) && (
                    <h5 className="d-f g-5 ai-c w-50">
                      {TRANSLATIONS.products.detail.sizeOnLabel}
                    </h5>
                  )}
                </div>
                <div className={'filters-content form-filter relative'}>
                  <div className={'wrapper-option'}>
                    <ul className="list-options d-f jc-sb ">
                      {optionsMapped.map((option) => (
                        <li
                          onClick={() => handleSelectOption(option)}
                          key={option?.label}
                          value={option?.value}
                          className={`${
                            option?.value === value ? 'info-tag' : 'action-tag'
                          } ${option.disabled ? 'option-disabled' : ''} d-f`}
                        >
                          <span style={{ width: '50%' }}>{option?.euSize}</span>
                          {option?.sizeOnLabel && (
                            <span style={{ width: '50%' }}>
                              {option?.sizeOnLabel}
                            </span>
                          )}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </OutsideClickHandler>
            </div>
          </div>
        </OutsideClickHandler>
      </div>
      <style jsx>{`
        :global(body) {
          overflow: ${isMobile && selectOpen ? 'hidden!important' : ''};
        }
        .select-background {
          background: rgba(0, 0, 0, 0.5);
          position: fixed;
          width: 100%;
          height: 100%;
          max-height: 100vh;
          border: none;
          border-radius: 0;
          z-index: 10;
          top: 0;
          left: 0;
          display: none;
        }
        .show-background {
          display: block;
        }
        .filters-container {
          background: var(--background);
          border: solid 1px var(--foreground);
          border-radius: 5px;
        }
        .options-divider {
          position: absolute;
          width: 1px;
          background-color: #dfe1e6;
          height: 100%;
          left: calc(50% - 15px);
        }
        .option-disabled {
          cursor: default !important;
          color: var(--disabled);
        }
        :global(.button.radius-button) {
          border-color: var(--grey-lighter);
        }
        .wrapper-filter {
          position: absolute;
          width: 100%;
          max-height: 520px;
          background: var(--background-utils);
          box-shadow: var(--shadow-light-hover);
          border-radius: 5px;
          z-index: 3;
          top: 55px;
          display: none;
        }
        .wrapper-filter-header {
          padding: 0.8rem;
          border-bottom: var(--line);
        }
        .wrapper-filter-header h5 {
          font-size: 12px;
          letter-spacing: 2px;
          font-weight: 700;
          text-transform: uppercase;
        }
        :global(.button.info-tag) {
          border-radius: 100px !important;
          border: solid thin var(--grey-lighter);
          font-weight: 600 !important;
          color: var(--foreground);
        }
        .info-tag {
          color: var(--primary);
        }
        ul li.info-tag::after {
          content: '✓';
          background: #ccc3;
          font-size: 14px;
          font-weight: 600;
          border-radius: 50px;
          width: 20px;
          height: 20px;
          text-align: center;
          position: absolute;
          right: 20px;
        }
        :global(.filters-content)::-webkit-scrollbar {
          display: none;
        }
        :global(.filters-content) {
          overflow: hidden scroll;
          max-height: 415px;
        }
        .label-filter {
          justify-content: space-between;
        }
        .form-filter {
          font-size: 16px;
        }
        :global(.filters-content) {
          overflow: hidden scroll;
          max-height: 415px;
        }
        .form-filter :global(label) {
          font-size: 13px;
          margin: 6px 0px 2px 0px;
        }
        .form-filter :global(input) {
          margin: 0px;
          max-height: 40px;
        }
        .wrapper-option {
          overflow: hidden scroll;
        }
        .list-options {
          display: flex;
          flex-direction: column;
          gap: 5px;
          padding: 0.6rem;
        }
        .list-options li {
          padding: 8px 4px;
          border-bottom: solid thin #ccc2;
          cursor: pointer;
        }
        .right-absolute {
          right: 0;
        }
        .show {
          display: flex;
        }
        @media screen and (max-width: 490px) {
          :global(.label-filter p),
          .btn-checkbox span {
            font-size: 14px !important;
          }
          :global(.filters-content) {
            overflow: hidden scroll;
            max-height: 100vh;
          }
          .wrapper-option {
            overflow: hidden scroll;
          }
          :global(.list-options li) {
            padding: 8px !important;
            font-size: 18px;
            border-bottom: solid thin #ccc2;
            cursor: pointer;
          }
          :global(.list-options) {
            padding: 0.8rem;
          }
          :global(form span) {
            font-size: 18px;
          }
          .wrapper-filter-header h5 {
            font-size: 14px;
            padding: 8px;
          }
          .submit-filters {
            position: fixed;
            width: 100%;
            left: 0;
            bottom: 0px;
            background: var(--background);
            border-top: var(--line);
            padding: 0.6rem;
            border-radius: 0px 0px 5px 5px;
          }
          .wrapper-filter {
            position: fixed;
            width: 100%;
            height: 100%;
            max-height: 100vh;
            background: var(--background-utils);
            box-shadow: var(--shadow-light-hover);
            border: none;
            border-radius: 0;
            z-index: 10;
            top: 100%;
            left: 0;
            transition: top 0.5s;
            display: flex;
            justify-content: flex-end;
            background-color: rgba(0, 0, 0, 0);
          }
          .show {
            top: 0;
            display: flex;
          }
        }
      `}</style>
    </>
  );
}

export default SizeSelector;
